import React, { Component, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { SubmissionError } from "redux-form";
import Subscription2FAForm, { FIELD_OTP } from "./Subscription2FAForm";
import SubscriptionForm, {
  FIELD_BIRTHDAY,
  FIELD_EMAIL,
  FIELD_FIRST_NAME,
  FIELD_LAST_NAME,
  FIELD_MOBILE_NUMBER,
  FIELD_POSTAL_CODE,
  FIELD_STORE_UUID,
} from "./SubscriptionForm";
import {
  Subscribe,
  Subscribe2FAFinalize,
  Subscribe2FAInit,
} from "landing/actions/SubscriptionActionCreators";
import type ISubscriptionData from "landing/forms/ISubscriptionData";
import type { LandingPageConfig } from "landing/objects/LandingPageConfig";
import type { Subscription } from "landing/objects/Subscription";
import type Subscription2FAInitResult from "landing/objects/Subscription2FAInitResult";
import SubscriptionAcknowledgement from "landing/objects/SubscriptionAcknowledgement";
import type SubscriptionResult from "landing/objects/SubscriptionResult";
import type { IMainAppState } from "main/reducers/IMainAppState";
import type { OAuthType } from "oauth/actions/OAuthActionCreators";
import Loader from "shared/loader/Loader";

interface ISubscriptionStepInnerProps extends ISubscriptionStepProps {
  Subscribe: (subscription: Subscription) => Promise<SubscriptionResult>;
  Subscribe2FAInit: (
    subscription: Subscription,
  ) => Promise<Subscription2FAInitResult>;
  Subscribe2FAFinalize: (
    subscription: Subscription,
    subscription2FAInitResult: Subscription2FAInitResult,
    otp: string,
  ) => Promise<SubscriptionResult>;
}

interface ISubscriptionStepInnerState {
  subscriptionResult?: SubscriptionResult;
  subscription2FAInitResult?: Subscription2FAInitResult;
  subscription?: Subscription;
  submitPromise?: Promise<void>;
}

class SubscriptionStepInner extends Component<
  ISubscriptionStepInnerProps,
  ISubscriptionStepInnerState
> {
  public state: ISubscriptionStepInnerState = {
    subscriptionResult: undefined,
    subscription2FAInitResult: undefined,
    subscription: undefined,
    submitPromise: undefined,
  };

  public onSubmit = async (formData: ISubscriptionData): Promise<void> => {
    const submitPromise: Promise<void> = (async () => {
      const { landingPageConfig } = this.props;

      // Get subscription from state or get from form.
      const subscription: Subscription = (() => {
        if (this.state.subscription) {
          return this.state.subscription!;
        }

        return {
          registrationRequestUUID: landingPageConfig.registrationRequestUUID,
          lpCode: landingPageConfig.lpCode,
          languageCode: this.props.languageCode,
          firstName: formData[FIELD_FIRST_NAME],
          lastName: formData[FIELD_LAST_NAME],
          mobileNumber: "1" + formData[FIELD_MOBILE_NUMBER],
          email: formData[FIELD_EMAIL] || undefined,
          dob: formData[FIELD_BIRTHDAY],
          postalCode: formData[FIELD_POSTAL_CODE],
          storeUUID: formData[FIELD_STORE_UUID] || undefined,
          userAgentJson: navigator.userAgent,
        };
      })();

      try {
        if (landingPageConfig.is2fa) {
          return await this.submit2FA(subscription, formData[FIELD_OTP]);
        }

        const { Subscribe } = this.props;
        const subscriptionResult: SubscriptionResult =
          await Subscribe(subscription);

        this.setState({
          subscriptionResult,
        });
      } catch (ex) {
        if ((ex as { message: string }).message === "MOBILE_LOCK_FAILED") {
          throw new SubmissionError({
            [FIELD_MOBILE_NUMBER]: (
              <FormattedMessage id="landing.subscription.field.mobile_number.validations.mobile_lock" />
            ),
          });
        }

        throw ex;
      }
    })();

    this.setState({
      submitPromise,
    });

    try {
      await submitPromise;
    } catch (ex) {
      if (ex instanceof SubmissionError) {
        throw ex;
      }
    }
  };

  private async submit2FA(
    subscription: Subscription,
    otp?: string,
  ): Promise<void> {
    // if 2fa request has never been initialized, or is forced to initialize again.
    // otp is undefined
    if (otp === undefined) {
      const { Subscribe2FAInit } = this.props;
      const subscription2FAInitResult: Subscription2FAInitResult =
        await Subscribe2FAInit(subscription);

      this.setState({
        subscription2FAInitResult,
        subscription,
      });

      return;
    }

    try {
      const { Subscribe2FAFinalize } = this.props;
      const { subscription2FAInitResult } = this.state;
      const subscriptionResult: SubscriptionResult = await Subscribe2FAFinalize(
        subscription,
        subscription2FAInitResult!,
        otp,
      );

      this.setState({
        subscriptionResult,
        subscription: undefined,
      });

      return;
    } catch (ex) {
      throw new SubmissionError({
        [FIELD_OTP]: (
          <FormattedMessage
            id="landing.subscription.2fa.invalid"
            values={{
              retryLink: (
                <a href="###" onClick={() => this.submit2FA(subscription)}>
                  <FormattedMessage id="landing.subscription.2fa.invalid.resend_code" />
                </a>
              ),
            }}
          />
        ),
      });
    }
  }

  public onGoOAuth = (oAuthType: OAuthType) => {
    const { onGoOAuth, landingPageConfig } = this.props;
    onGoOAuth(oAuthType, landingPageConfig.lpCode);
  };

  public render(): ReactNode {
    const { landingPageConfig } = this.props;
    const { subscriptionResult } = this.state;

    // If subscription completes.
    if (subscriptionResult) {
      return (
        <SubscriptionAcknowledgement
          landingPageConfig={landingPageConfig}
          subscriptionResult={subscriptionResult!}
        />
      );
    }

    const { submitPromise, subscription2FAInitResult } = this.state;

    // If nothing has been entered (no 2fa result).
    if (!subscription2FAInitResult) {
      return (
        <>
          <SubscriptionForm
            landingPageConfig={landingPageConfig}
            onGoOAuth={this.onGoOAuth}
            onSubmit={this.onSubmit}
          />

          {submitPromise && <Loader promise={submitPromise} />}
        </>
      );
    } else {
      return (
        <>
          <Subscription2FAForm
            landingPageConfig={landingPageConfig}
            onSubmit={this.onSubmit}
          />

          {submitPromise && <Loader promise={submitPromise} />}
        </>
      );
    }
  }
}

export interface ISubscriptionStepProps {
  landingPageConfig: LandingPageConfig;
  languageCode: string;
  onGoOAuth: (oAuthType: OAuthType, lpCode: string) => void;
}

export default connect(
  (state: IMainAppState, props: ISubscriptionStepProps) => ({}),
  (dispatch) =>
    bindActionCreators(
      {
        Subscribe,
        Subscribe2FAInit,
        Subscribe2FAFinalize,
      },
      dispatch,
    ),
)(SubscriptionStepInner);
